'use client'

import { Button } from '@lastbrand/web-ui/button'
import { useTranslation } from 'next-i18next'
import { useMemo, useRef, useState } from 'react'

import { WishlistGridDs, wishListItemWrapperCss } from '@src/components/blocks/wishlist-item-list-ds/wishlist-grid-ds'
import { SvdProductCard } from '@src/components/svd-product-card'
import { openSvdQuickAdd } from '@src/components/svd-product-card/add-to-bag/svd-quick-add'
import { getInitialSvdVariant, getSvdCardListKey } from '@src/components/svd-product-card/svd-product-card.helpers'
import { getSvdApplicableTags } from '@src/components/svd-product-card/svd-product-card.promo'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import type { SvdNativeCardVariant } from '@src/types/svd-product-card.types'

import { SvdWishlistCard } from './svd-wishlist.types'

const WISHLIST_DISPLAY_NAME = 'Wishlist'
const WISHLIST_TRACKER = 'wishlist'
const TRACK_CATEGORY = 'product-list'

/**
 * SVD wishlist grid, **DS layout** (STRFRNT-16908).
 *
 * The SVD list used to have only one body, wearing the legacy SCSS grid. On a DS page that grid sized
 * its columns in `rem` for the SCSS card while `SvdProductCard` switched to its DS body — which
 * deliberately ignores `maxWidth`/`className` because sizing belongs to the grid — so the tiles
 * rendered oversized and unaligned against the non-SVD DS wishlist.
 *
 * This body reuses `WishlistGridDs`, the very same band/grid/loader/empty-state shell the non-SVD DS
 * list renders, so the two are aligned by construction rather than by two copies of the same tokens.
 * The only differences from that list are the tile (`SvdProductCard`, whose DS body this triggers) and
 * the add-to-bag action (`openSvdQuickAdd`, Path A — the SVD data shape has no `IProductCard` to hand
 * `productBuyNowStateManager`).
 *
 * NOTE: no `maxWidth` and no `className` on the card. Adding either re-introduces the bug.
 */
function WishlistItemListSvdDs({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist = false,
}: {
  items: SvdWishlistCard[] | undefined
  isLoading?: boolean
  sectionConfig: { tracker?: string }
  isSelfWishlist?: boolean
}) {
  const { t: tCommon } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  // Per-item selected variant, driven by the card's `onVariantChange` (swatch pick on the wishlist tile).
  const [selectedVariants, setSelectedVariants] = useState<Record<string, SvdNativeCardVariant>>({})

  // Newest first, matching the legacy and DS lists.
  const reversedItems = useMemo(() => (items ? [...items].reverse() : []), [items])

  // Bumps whenever `items` is replaced by a fresh fetch (every successful fetch is a brand-new array —
  // `useFetch` always calls `setResponse(freshArray)`, never appends). Folded into each tile's REACT
  // key (not `uniqueKey` itself, which stays stable for the testid/selectedVariants lookups below) so
  // a fresh fetch forces every tile to remount.
  //
  // Why this is needed: `SvdProductCard`'s local swatch pick (`userVariantId` in
  // `use-svd-product-card.ts`) only resets when ITS OWN `selectedVariantId` prop changes. Preview a
  // DIFFERENT color on an existing tile, then heart that color — a NEW sibling entry is created for
  // it (no server-side duplicate), but the ORIGINAL tile's own `selectedVariantId` never changed, and
  // its stale swatch pick is STILL a valid member of `cardVariants` (just another color of the SAME
  // product), so the hook's own "self-heals when the picked id is no longer a member" premise never
  // fires. The original tile keeps showing the previewed color forever, visually matching its new
  // sibling — looking exactly like a duplicated wishlist entry when no such duplicate ever existed
  // (STRFRNT-17133, third occurrence). Forcing a remount on every wishlist refetch is the fix: it
  // can't leave a stale local pick behind because there's no old fiber to leave it on.
  //
  // TODO(STRFRNT-17133 follow-up): this is a blunt instrument — it resets every tile's local state on
  // every refetch, not just the one tile that actually needs it, because "a sibling was just created"
  // is a fact about the LIST, not about any individual card's own props. The real fix is making
  // `SvdProductCard` support a genuinely controlled `selectedVariant`/`onVariantChange` pair (opt-in,
  // so the ~20 other render sites relying on today's pseudo-controlled/persist-across-re-renders
  // behavior are untouched) and having this list feed its own `selectedVariants` state (already
  // tracked below for the "Add to Bag" button) straight into the card instead of letting the card own
  // an internal pick that can go stale. That eliminates the whole class of bug instead of papering
  // over one instance of it.
  const itemsRef = useRef(items)
  const generationRef = useRef(0)
  if (itemsRef.current !== items) {
    itemsRef.current = items
    generationRef.current += 1
  }

  return (
    <WishlistGridDs isLoading={isLoading} hasData={reversedItems.length > 0}>
      {reversedItems.map(({ product, selectedVariantId }) => {
        // Same key every collection/PLP SVD grid uses (`getSvdCardListKey`), and for the same reason:
        // the SAME productId can occupy two sibling tiles here (a product wishlisted in two colors),
        // so the key must be per-*card* (productId + the wishlisted variant's own selectedAttributes),
        // not per-variantId — a raw `variantId` isn't guaranteed to vary 1:1 with the wishlisted color,
        // and a collision here made two tiles for the same product share React identity, letting one
        // tile's swatch-selection state leak onto the other (STRFRNT-17133).
        const uniqueKey = getSvdCardListKey(product)
        const initialVariant = getInitialSvdVariant(product.cardVariants, selectedVariantId)
        const trackConfig: TrackConfig = {
          trackInternal: true,
          trackGA: true,
          // @i18n-ignore
          category: TRACK_CATEGORY,
          action: `${tracker ?? WISHLIST_TRACKER}__click_product_wishlist`,
          value: product.relatedIds.spid.toString(),
          context: {
            contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
            contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
            // @i18n-ignore
            contentContainerDisplayName: WISHLIST_DISPLAY_NAME,
            findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
          },
        }

        return (
          <div
            key={`${uniqueKey}_${generationRef.current}`}
            className={wishListItemWrapperCss}
            data-testid={TEST_LOCATORS.WISHLIST_LIST.ITEM(uniqueKey)}
            onClick={() => {
              setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
            }}
          >
            <SvdProductCard
              product={product}
              selectedVariantId={selectedVariantId}
              hideBuyNow
              showDeleteBtn={isSelfWishlist}
              hideDeleteForOtherColors
              productClickTrackConfig={trackConfig}
              onVariantChange={variant => {
                setSelectedVariants(prev => ({
                  ...prev,
                  [uniqueKey]: variant,
                }))
              }}
            />
            {/* Desktop-only, breakpoint-hidden per DS convention (the SCSS body uses a UA device
                check instead). Styling matches the non-SVD DS list's button exactly. */}
            <Button
              variant="tertiary"
              size="lg"
              data-testid={TEST_LOCATORS.WISHLIST_LIST.ADD_TO_CART(uniqueKey)}
              css={{
                display: { base: 'none', lg: 'block' },
                w: 'full',
                mt: 'space-16',
                // Hover adopts the primary variant's resting look (matches the legacy orange hover).
                _hover: { bg: 'cantaloupe-100', color: 'black', boxShadow: 'none' },
              }}
              onClick={() => {
                const currentVariant = selectedVariants[uniqueKey] ?? initialVariant
                openSvdQuickAdd({
                  product,
                  selectedVariant: currentVariant,
                  applicableTags: getSvdApplicableTags(currentVariant),
                  trackConfig,
                })
              }}
            >
              {(selectedVariants[uniqueKey] ?? initialVariant)?.appExclusive
                ? tCommon('shop_in_the_app').toUpperCase()
                : tCommon(atcLabel)}
            </Button>
          </div>
        )
      })}
    </WishlistGridDs>
  )
}

export default WishlistItemListSvdDs
